import { useFullReleaseStrict, useUpdateFullRelease } from "@/common/hooks_useFullRelease";
import { GraphNode, Trigger } from "@packages/commons";
import { releaseService } from "@/app/providers/services";
import { useNotify } from "@/common/notifications";
import { v4 as uuidv4 } from "uuid";
import { pick } from "lodash";

export const NONE_ENUM = "none";
export const ATTRIBUTE_TYPES = [
  { value: "auto", key: "Auto" },
  { value: "boolean", key: "Boolean" },
  { value: "currency", key: "Currency" },
  { value: "date", key: "Date" },
  { value: "datetime", key: "Date Time" },
  { value: "file", key: "File" },
  { value: "number", key: "Number" },
  { value: "text", key: "Text" },
  { value: "timeofday", key: "Time" },
];

export interface AttributeInfoFormValues {
  id: string;
  type: string;
  enum: string;
  publicId: string;
  category: boolean;
  categoryName: string;
  categoryTitle: boolean;
  phase: boolean;
  phaseName: string;
  // triggerEnrichments: boolean;
  tags: Array<string>;
  explanation: string;
  label: string;
  labelDisplay: string;
  triggers: Record<string, Trigger>;
}

const removeEmpty = (obj: any, allowed: string[] = []): any => {
  const result: any = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const isAllowed = allowed.includes(key);
      if (typeof obj[key] === "boolean" || (typeof obj[key] === "string" && (obj[key].length > 0 || isAllowed)) || typeof obj[key] === "number") {
        result[key] = obj[key];
      } else if (Array.isArray(obj[key])) {
        const res = obj[key].map((item) => removeEmpty(item));
        if (res.length > 0 || isAllowed) {
          result[key] = res;
        }
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        const res = removeEmpty(obj[key]);
        if (Object.keys(res).length > 0 || isAllowed) {
          result[key] = res;
        }
      }
    }
  }

  return result;
};

const filter = ["type", "enum", "publicId", "triggers", "tags", "category", "categoryName", "categoryTitle", "phase", "phaseName", "explanation", "controlDefaults"];
const allowEmpty = ["triggers", "publicId"];

const initValues = (node: GraphNode): AttributeInfoFormValues => {
  const triggers = node.triggers ?? [];
  // handle legacy values
  if (node.enrichment) {
    triggers.push(node.enrichment);
  }
  return {
    id: node.id,
    type: node.type,
    enum: node.enum || NONE_ENUM,
    publicId: node.publicId || "",
    tags: node.tags || [],
    category: node.category ?? false,
    categoryName: node.categoryName ?? "",
    categoryTitle: node.categoryTitle ?? false,
    phase: node.phase ?? false,
    phaseName: node.phaseName ?? "",
    // triggerEnrichments: Boolean(node.enrichment),
    explanation: node.explanation || "",
    // @ts-ignore
    label: node.controlDefaults?.label ?? "",
    // @ts-ignore
    labelDisplay: node.controlDefaults?.labelDisplay ?? "",
    // map triggers to object with uuids, need this so we can delete triggers
    triggers: Object.fromEntries(triggers.map((trigger) => [uuidv4(), trigger])),
  };
};

export const useAttributeForm = (node: GraphNode) => {
  const notify = useNotify();
  const release = useFullReleaseStrict();
  const updateRelease = useUpdateFullRelease();
  const service = releaseService.useSubresource("attribute", release.id);
  const query = service.useMutateResource(node, {
    onError: (error: any) => {
      if (error.message) {
        notify.error(error.message, 5000);
      } else {
        notify.error("An error has occurred in the backend. Contact Support", 5000);
      }
    },
    onSuccess: () => {
      updateRelease();
    }
  });

  const preSubmit = (values: AttributeInfoFormValues) => {
      const marshalledTriggers = Object.values(values.triggers).map((trigger: any) => {
        if (trigger.type === "comms") {
          const nextTrigger = { ...trigger };
          const whoMethod = nextTrigger.whoMethod || "attribute";

          if (whoMethod === "attribute") {
            delete nextTrigger.whoIntegration;
            delete nextTrigger.whoOrgModel;
          } else if (whoMethod === "integration") {
            delete nextTrigger.who;
            delete nextTrigger.whoOrgModel;
          } else if (whoMethod === "orgmodel") {
            delete nextTrigger.who;
            delete nextTrigger.whoIntegration;
            nextTrigger.whoIsContact = true;
          }

          return nextTrigger;
        }

        if (trigger.type !== "task") return trigger;

        const nextTrigger = { ...trigger };
        const assignmentMethod = nextTrigger.assignmentMethod || "attribute";

        if (assignmentMethod === "attribute") {
          delete nextTrigger.assignmentIntegration;
          delete nextTrigger.assignmentOrgModel;
          delete nextTrigger.assignmentSubmitterField;
        } else if (assignmentMethod === "integration") {
          delete nextTrigger.assignment;
          delete nextTrigger.assignmentOrgModel;
        } else if (assignmentMethod === "orgmodel" || assignmentMethod === "orgUnits") {
          delete nextTrigger.assignment;
          delete nextTrigger.assignmentIntegration;
        }

        return nextTrigger;
      })

      // here we have raw form data, before any other transforms
      return {
        ...values,
        // map controlDefaults to the correct place, the filter will remove it from the root
        controlDefaults: {
          label: values.label,
          labelDisplay: values.labelDisplay,
        },
        // remove enum if it is none
        ...((values.enum === NONE_ENUM) ? { enum: null } : {}),
        // remove enrichments if they are not enabled
        // ...(values.triggerEnrichments === false ? { enrichment: null } : {}),
        // always remove enrichment, as they are deprecated
        enrichment: undefined,
        // map triggers back to array
        triggers: marshalledTriggers,
      };
  };

  const submit = async (values: AttributeInfoFormValues) => {
    const submitted = preSubmit(values);
    const filtered = pick(submitted, filter);
    const data = removeEmpty(filtered, allowEmpty);
    await query.action(data);
  };

  return {
    query,
    defaultValues: initValues(node),
    submit,
  };
};
